import React, { useState } from "react";
import { Box, HStack, Text, Typography, Pressable } from "components/ui";
import { HelpCircle } from "lucide-react-native";
import { Platform } from "react-native";

interface ESGMetricCardProps {
	title: string;
	currentValue: number;
	previousValue: number;
	unit?: string;
	showPercentage?: boolean;
	onInfoClick?: () => void;
}

const ESGMetricCard: React.FC<ESGMetricCardProps> = ({
	title,
	currentValue,
	previousValue,
	unit = "",
	showPercentage = false,
	onInfoClick,
}) => {
	const [showTooltip, setShowTooltip] = useState(false);

	// Calculate the maximum value for scaling the bars
	const maxValue = Math.max(currentValue, previousValue);
	
	// Calculate bar widths as percentages
	const currentBarWidth = maxValue > 0 ? (currentValue / maxValue) * 100 : 0;
	const previousBarWidth = maxValue > 0 ? (previousValue / maxValue) * 100 : 0;

	// Format the display value
	const formatValue = (value: number) => {
		const roundedValue = Math.round(value * 100) / 100; // Round to 2 decimal places
		const formattedValue = roundedValue.toLocaleString(); // Add commas for thousands
		if (showPercentage) {
			return `${formattedValue}%`;
		}
		return `${formattedValue}${unit}`;
	};

	// Only show hover functionality on web
	const isWeb = Platform.OS === "web";

	return (
		<Box mb={16}>
			<HStack alignItems="center" mb={8} justifyContent="space-between" width="100%">
				<HStack alignItems="center" flex={1} width="35%">
					<Text fontSize={14} fontWeight="500" color="$textDark" textAlign="left">
						{title}
					</Text>
					{onInfoClick && (
						<Pressable onPress={onInfoClick} ml={8}>
							<HelpCircle size={16} color="#666" />
						</Pressable>
					)}
				</HStack>
				<Box width="65%">
					<Box mb={2} position="relative">
						<Pressable
							onPressIn={isWeb ? () => setShowTooltip(true) : undefined}
							onPressOut={isWeb ? () => setShowTooltip(false) : undefined}
							onHoverIn={isWeb ? () => setShowTooltip(true) : undefined}
							onHoverOut={isWeb ? () => setShowTooltip(false) : undefined}
						>
							<Box
								height={5}
								bgColor="#FF6B35"
								width={`${previousBarWidth}%`}
								borderRadius={1}
							/>
						</Pressable>
					</Box>
			
					<Box mb={4} position="relative">
						<Pressable
							onPressIn={isWeb ? () => setShowTooltip(true) : undefined}
							onPressOut={isWeb ? () => setShowTooltip(false) : undefined}
							onHoverIn={isWeb ? () => setShowTooltip(true) : undefined}
							onHoverOut={isWeb ? () => setShowTooltip(false) : undefined}
						>
							<Box
								height={24}
								bgColor="#007AFF"
								width={`${currentBarWidth}%`}
								borderRadius={4}
								justifyContent="center"
								alignItems="flex-end"
								px={10}
							>
								<Text fontSize={12} color="white" fontWeight="500" numberOfLines={1}>
									{formatValue(currentValue)}
								</Text>
							</Box>
						</Pressable>
					</Box>
					
				</Box>
			</HStack>
			
			
			{/* Combined tooltip showing both values */}
			{showTooltip && isWeb && (
				<Box
					position="absolute"
					top={-60}
					left="50%"
					bgColor="#333"
					px={12}
					py={8}
					borderRadius={4}
					zIndex={1000}
					style={{ transform: [{ translateX: -50 }] }}
				>
					<Box>
						<HStack justifyContent="space-between" mb={3} gap={16}>
							<Text fontSize={11} color="white" fontWeight="500">
								Previous year
							</Text>
							<Text fontSize={12} color="white">
								{formatValue(previousValue)}
							</Text>
						</HStack>
						<HStack justifyContent="space-between" gap={16}>
							<Text fontSize={11} color="white" fontWeight="500">
								Current year
							</Text>
							<Text fontSize={12} color="white">
								{formatValue(currentValue)}
							</Text>
						</HStack>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default ESGMetricCard;
